/**
 * Mobile nAVigation module (MAV)
 * Shows and hides element groups when a trigger element gets clicked. Elements are placed right in the body to maintain the device's natural scroll behavior. For an example, see: http://www.posch.com (view on mobile)
 *
 * Used in conjunction with styles/objects/_mav.scss
 *
 * Required HTML markup:
 * ```
 * <body>
 *     <div class="en-mav-level en-mav-level--root">
 *         <section>This element will not be hidden.</section>
 *         <nav class="en-mav-trigger">Example trigger</nav>
 *         <section class="en-mav-element">
 *             This element will be hidden.
 *             <nav class="en-mav-trigger">Example trigger (nested)</nav>
 *             <section class="en-mav-element">This (nested) element will be hidden.</section>
 *         </section>
 *         <nav class="en-mav-trigger">Example header</nav>
 *         <section class="en-mav-element">This element will be hidden.</section>
 *     </div>
 *     <section>This element is unaffected (e.g. modals, lightboxes, …)</section>
 *     <div class="en-mav-animator">
 *         <div class="en-mav-animator__inner">
 *             <div class="en-mav-header">Back</div>
 *         </div>
 *     </div>
 * </body>
 * ```
 *
 * Implementation example:
 * ```
 * EN.Mav.init();
 * ```
 *
 * @module Mav
 * @requires UrlHelper
 * @author EN GARDE (Martin Holler)
 * @version 3.4
 *
 * Changelog:
 * 	3.4		added the ability to specify the target element using the "data-en-mav-element" attribute
 * 	3.3.1	fixed scroll position on chrome, added isActive function
 * 	3.0		rewrite
 * 	2.0		rewrite
 * 	1.0		initial version
 */

var EN = EN || {};

EN.Mav = (function ($, _) {
	'use strict';

	var animating = false;
	var id = 0;
	var $rootLevel,
		$generatedLevel,
		$animator,
		$movePlaceholder,
		$animationPlaceholder;

	var options = {
		animate: true,
		swipeSupport: true,
		classNames: {
			level: 'en-mav-level',
			rootLevel: 'en-mav-level--root',
			generatedLevel: 'en-mav-level--generated',
			trigger: 'en-mav-trigger',
			element: 'en-mav-element',
			animator: 'en-mav-animator__inner',
			header: 'en-mav-header',
			placeholder: 'en-mav-placeholder',
			anchor: 'en-anchor',

			// state classes
			movingIn: 'en-is-moving-in',
			movingOut: 'en-is-moving-out'
		},
		idPrefix: 'en-mav-',
		scrollInner: false
	};


	/**
	 * Sets a unque id attribute to an element. If the element or its anchor already has an id, it is not set.
	 * @param {jQuery} $element
	 * @return {string} – The element's id.
	 */
	var _setUniqueId = function ($element) {
		var elementId = '';

		if ($element && $element.length) {
			elementId = $element.children('.' + options.classNames.anchor).attr('id') || $element.attr('id');

			// if the element does not have an id yet, generate one
			if (!elementId) {
				elementId = options.idPrefix + id;
				id++;

				$element.attr('id', elementId);
			}
		}

		return elementId;
	};

	/**
	 * Puts a given placeholder element after a given element.
	 * @param {jQuery} $element
	 * @param {jQuery} $placeholder
	 * @return {jQuery}
	 */
	var _setPlaceholder = function ($element, $placeholder) {
		if (!$element || !$element.length || !$placeholder || !$placeholder.length) {
			return;
		}

		$element.after($placeholder);

		return $element;
	};

	/**
	 * Moves an element back to a given placeholder's position and them removes the placeholder from the DOM.
	 * @param {jQuery} $element
	 * @param {jQuery} $placeholder
	 * @return {jQuery}
	 */
	var _returnToPlaceholder = function ($element, $placeholder) {
		if (!$element || !$element.length || !$placeholder || !$placeholder.length) {
			return;
		}

		$placeholder.after($element.removeAttr('style')).remove();

		return $element;
	};

	/**
	 * Moves a given element to a new level. If no level exists yet, it will create one.
	 * @param {jQuery} $element
	 * @param {boolean} [isBelowCurrentLevel] – This parameter is only needed if animations are active.
	 * @return {jQuery}
	 */
	var _moveElement = function ($element, isBelowCurrentLevel) {
		if (!$element || !$element.length) {
			return;
		}

		var $existingElement, $header, $scroller;

		// if there is no generated level yet, create one
		if (!$generatedLevel) {
			$header = $animator.children('.' + options.classNames.header).clone();
			$generatedLevel = $('<div class="' + options.classNames.level + ' ' + options.classNames.generatedLevel +  '" />');
			$generatedLevel.append($header);
			$rootLevel.after($generatedLevel);

			if (options.swipeSupport && $.fn.swipe) {
				$generatedLevel.swipe({
					swipeRight: function () {
						$header.trigger('click');
					}
				});
			}
		}

		$scroller = options.scrollInner ? $generatedLevel : $(window);
		$existingElement = $generatedLevel.children('.' + options.classNames.element);

		// save the current scroll position to the existing element
		if ($existingElement.length) {
			$existingElement.data('scrollPosition', $scroller.scrollTop());
		}
		else {
			$rootLevel.data('scrollPosition', options.scrollInner ? $rootLevel.scrollTop() : $scroller.scrollTop());
		}

		// if there is already an element in the generated level, put it back to its original place
		_returnToPlaceholder($existingElement, $movePlaceholder);

		// before moving the element, put a placeholder to its position
		_setPlaceholder($element, $movePlaceholder);

		// move the element to the generated level and show it
		$generatedLevel.append($element.show());

		// hide the root level
		$rootLevel.hide();

		// restore the scroll position
		if (isBelowCurrentLevel) {
			$scroller.scrollTop($element.data('scrollPosition'));
		}
		else {
			$scroller.scrollTop(0);
		}

		return $element;
	};

	/**
	 * Animates the `$animator` element and runs a callback. Warning: The callback might be called before the animation even starts, depending on the `direction` parameter.
	 * @param {jQuery} $element
	 * @param {string} [direction="in"] - Whether to animate the element "in" or "out".
	 * @param {function} [callback]
	 * @return {jQuery}
	 */
	var _animate = function ($element, direction, callback) {
		if (!$element || !$element.length) {
			return;
		}

		// if another animation is still running, cancel it
		if (animating) {
			$animator.trigger('mavAnimationCancel');
		}

		direction = direction || 'in';

		var scrollPosition = options.scrollInner ? ($generatedLevel ? $generatedLevel.scrollTop() : 0) : $(window).scrollTop();
		var $inactiveLevel;

		$element.trigger('mavBeforeMove', {
			direction: direction
		});

		if (direction !== 'in' && typeof callback === 'function') {
			callback();
		}

		$inactiveLevel = $generatedLevel || $rootLevel;

		// move the element to the animator
		_setPlaceholder($element, $animationPlaceholder);
		$animator.append($element.show());

		// apply the current scroll position to the animator
		if (direction === 'in') {
			$animator.scrollTop(0);
		}
		else {
			$animator.scrollTop(scrollPosition);
		}

		// start the animation
		$animator.addClass(direction === 'in' ? options.classNames.movingIn : options.classNames.movingOut);
		$inactiveLevel.addClass(direction === 'in' ? options.classNames.movingOut : options.classNames.movingIn);
		animating = true;

		// when the animation is done, restore everything and run the callback
		$animator.one('transitionend webkitTransitionEnd mavAnimationCancel', function () {

			// remove the event listeners again for browsers that support both versions
			$animator.off('transitionend webkitTransitionEnd mavAnimationCancel');

			$animator.removeClass(options.classNames.movingIn + ' ' + options.classNames.movingOut);
			$inactiveLevel.removeClass(options.classNames.movingIn + ' ' + options.classNames.movingOut);

			_returnToPlaceholder($element, $animationPlaceholder);

			if (direction === 'in' && typeof callback === 'function') {
				callback();
			}

			animating = false;

			$element.trigger('mavAfterMove', {
				direction: direction
			});
		});

		return $element;
	};

	/**
	 * Returns to the root level.
	 * @memberof module:Mav
	 * @param {boolean} animate – Whether or not to run the animation.
	 * @return {jQuery}
	 */
	var toRoot = function (animate) {
		if (!$generatedLevel) {
			return;
		}

		if (animate !== false) {
			animate = options.animate;
		}

		var $element = $generatedLevel.children('.' + options.classNames.element);

		EN.UrlHelper.setHash('');

		if (animate) {
			_animate($element, 'out', function () {
				_returnToPlaceholder($element, $movePlaceholder);

				$generatedLevel.remove();
				$generatedLevel = undefined;
				$rootLevel.show();

				if (!options.scrollInner && $rootLevel.data('scrollPosition')) {
					$(window).scrollTop($rootLevel.data('scrollPosition'));
				}
			});
		}
		else {
			_returnToPlaceholder($element, $movePlaceholder);

			$generatedLevel.remove();
			$generatedLevel = undefined;
			$rootLevel.show();

			if (!options.scrollInner && $rootLevel.data('scrollPosition')) {
				$(window).scrollTop($rootLevel.data('scrollPosition'));
			}

			$element.trigger('mavBeforeMove mavAfterMove');
		}

		return $rootLevel;
	};

	/**
	 * Displays a specified element.
	 * @memberof module:Mav
	 * @param {jQuery} $element
	 * @param {boolean} animate – Whether or not to run the animation.
	 * @return {jQuery}
	 */
	var toElement = function ($element, animate) {
		if (!$element || !$element.length || !$element.hasClass(options.classNames.element) || $element.is(':visible') || !isActive()) {
			return;
		}

		if (animate !== false) {
			animate = options.animate;
		}

		var isAboveCurrentLevel = !!$element.closest('.' + options.classNames.generatedLevel).length || !$generatedLevel;
		var id = $element.children('.' + options.classNames.anchor).attr('id') || $element.attr('id');

		EN.UrlHelper.setHash(id);

		if (animate) {
			// if the element is above the current level, animate the element in
			if (isAboveCurrentLevel) {
				_animate($element, 'in', function () {
					_moveElement($element, !isAboveCurrentLevel);
				});
			}
			// otherwise, animate the current element out
			else {
				_animate($generatedLevel.children('.' + options.classNames.element), 'out', function () {
					_moveElement($element, !isAboveCurrentLevel);
				});
			}
		}
		else {
			_moveElement($element, !isAboveCurrentLevel);
			$element.trigger('mavBeforeMove').trigger('mavAfterMove');
		}

		return $element;
	};

	/**
	 * Caches elements, sets up all event listeners and sets options.
	 * @param {object} [settings] - Overrides defaults, see below.
	 * @property {boolean} animate=true - Whether or not to run animations.
	 * @property {boolean} swipeSupport=true - Whether or swipe gestures are supported (requires touchSwipe.js).
	 * @property {object} classNames - Collection of class names that are set by the module.
	 * @property {string} idPrefix="en-mav-" - Prefix for all ids set my Mav.
	 * @property {boolean} scrollInner=false - Set this to true if not the body but .en-mav-level is the scrollable element.
	 * @memberof module:Mav
	 */
	var init = function (settings) {
		options = $.extend({}, options, settings);

		// trigger click
		$(document).on('click', '.' + options.classNames.trigger, function (e) {
			e.preventDefault();

			var $this = $(this);
			var $element = $this.attr('data-en-mav-element') ? $($this.attr('data-en-mav-element')).first() : $this.next('.' + options.classNames.element);

			_setUniqueId($element);
			toElement($element);
		});

		// back button click
		$(document).on('click', '.' + options.classNames.header, function () {
			var $element = $movePlaceholder.closest('.' + options.classNames.element);

			if ($element.length) {
				_setUniqueId($element);
				toElement($element);
			}
			else {
				toRoot();
			}
		});

		// history
		$(window).on('popstate', function (e) {
			if (animating) {
				$animator.trigger('mavAnimationCancel');
			}

			if (window.location.hash) {
				var $element = $(window.location.hash);

				if ($element.hasClass(options.classNames.anchor) && $element.parent().attr('id')) {
					$element = $element.parent();
				}

				toElement($element);
			}
			else {
				toRoot();
			}
		});

		// go to root if the window size exceeds the breakpoint
		$(window).on('resize', _.debounce(function () {
			if (!isActive()) {
				toRoot(false);
			}
		}, 200));

		// ready
		$(function () {
			var $element;

			// cache elements
			$rootLevel = $('.' + options.classNames.rootLevel);
			$animator = $('.' + options.classNames.animator);
			$movePlaceholder = $('<div class="' + options.classNames.placeholder + '" />');
			$animationPlaceholder = $('<div class="' + options.classNames.placeholder + '" />');

			// check hash
			if (window.location.hash) {
				$element = $(window.location.hash);

				if ($element.hasClass(options.classNames.anchor) && $element.parent().attr('id')) {
					$element = $element.parent();
				}

				toElement($element, false);
			}

			// disable history scroll restoration
			// @link http://stackoverflow.com/questions/10742422/prevent-browser-scroll-on-html5-history-popstate
			if (history.scrollRestoration && isActive()) {
				history.scrollRestoration = 'manual';
			}
		});
	};

	/**
	 * Is browser currently in a state that shows Mav
	 * @return {boolean}
	 * @memberof module:Mav
	 */
	var isActive = function () {
		return $animator && $animator.is(':visible');
	};

	return {
		init: init,
		toElement: toElement,
		toRoot: toRoot,
		isActive: isActive
	};
}(jQuery, _));
